"use client";

import { use } from "react";
import { useAction, useLoader } from "@rangojs/router/client";
import { removeFromCart, updateCartItem } from "../actions/cart.js";
import { CartLoader } from "../loaders/cart.js";

function QuantityControl({ itemId, quantity }: { itemId: string; quantity: number }) {
  const { state: updateState, payload: updatePayload } = useAction(updateCartItem);
  const { state: removeState, payload: removePayload } = useAction(removeFromCart);

  const isUpdating =
    updateState === "loading" &&
    updatePayload instanceof FormData &&
    updatePayload.get("itemId") === itemId;
  const isRemoving =
    removeState === "loading" &&
    removePayload instanceof FormData &&
    removePayload.get("itemId") === itemId;
  const isLoading = isUpdating || isRemoving;

  return (
    <div className="flex items-center gap-1">
      {quantity <= 1 ? (
        <form action={removeFromCart}>
          <input type="hidden" name="itemId" value={itemId} />
          <button
            type="submit"
            disabled={isLoading}
            className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            aria-label="Remove item"
          >
            &times;
          </button>
        </form>
      ) : (
        <form action={updateCartItem}>
          <input type="hidden" name="itemId" value={itemId} />
          <input type="hidden" name="quantity" value={quantity - 1} />
          <button
            type="submit"
            disabled={isLoading}
            className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            aria-label="Decrease quantity"
          >
            &minus;
          </button>
        </form>
      )}

      <span className="w-8 text-center text-sm font-medium text-slate-900">{quantity}</span>

      <form action={updateCartItem}>
        <input type="hidden" name="itemId" value={itemId} />
        <input type="hidden" name="quantity" value={quantity + 1} />
        <button
          type="submit"
          disabled={isLoading}
          className="flex h-8 w-8 items-center justify-center rounded border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          aria-label="Increase quantity"
        >
          +
        </button>
      </form>
    </div>
  );
}

function RemoveButton({ itemId }: { itemId: string }) {
  const { state, payload } = useAction(removeFromCart);
  const isRemoving =
    state === "loading" && payload instanceof FormData && payload.get("itemId") === itemId;

  return (
    <form action={removeFromCart} className="mt-1">
      <input type="hidden" name="itemId" value={itemId} />
      <button
        type="submit"
        disabled={isRemoving}
        className="text-xs text-slate-400 hover:text-red-600 disabled:opacity-50"
      >
        {isRemoving ? "Removing..." : "Remove"}
      </button>
    </form>
  );
}

export function CartContents() {
  const { data } = useLoader(CartLoader);
  const cart = data.cart ? use(data.cart) : null;

  if (!cart || !cart.productItems?.length) {
    return <p className="text-slate-500">Your cart is empty.</p>;
  }

  const items = cart.productItems ?? [];

  return (
    <>
      <div className="divide-y divide-slate-200">
        {items.map((item: any) => (
          <div key={item.itemId} className="flex items-center gap-4 py-4">
            {item.image?.link && (
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded bg-slate-100">
                <img
                  src={item.image.link}
                  alt={item.productName ?? ""}
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <div className="flex-1">
              <p className="font-medium text-slate-900">{item.productName}</p>
              {item.price != null && (
                <p className="text-sm text-slate-500">
                  {cart.currency ?? "GBP"} {item.price.toFixed(2)} each
                </p>
              )}
            </div>

            <QuantityControl itemId={item.itemId} quantity={item.quantity} />

            <div className="w-24 text-right">
              {item.price != null && (
                <p className="font-medium text-slate-900">
                  {cart.currency ?? "GBP"} {(item.price * item.quantity).toFixed(2)}
                </p>
              )}
              <RemoveButton itemId={item.itemId} />
            </div>
          </div>
        ))}
      </div>

      {cart.productSubTotal != null && (
        <div className="mt-6 border-t border-slate-200 pt-4 text-right">
          <p className="text-lg font-semibold text-slate-900">
            Subtotal: {cart.currency ?? "GBP"} {cart.productSubTotal.toFixed(2)}
          </p>
        </div>
      )}
    </>
  );
}
